import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import { DataGrid, GridToolbarQuickFilter, enUS } from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialogComponent from "./ConfirmDialogComponent";
import { useTranslation } from "react-i18next";

import { NAV_BG, AMBER } from '../theme';

function CustomToolbar() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <GridToolbarQuickFilter
        variant="outlined"
        size="small"
        placeholder={t('teachers.search')}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.85rem',
            borderRadius: '8px',
            background: '#f8fafc',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: AMBER },
            '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 1.5 },
          },
          '& .MuiInputBase-input': { py: 0.75 },
        }}
      />
    </Box>
  );
}

const TeachersComponent = (props) => {
  const { authTokens, logoutUser, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [teacherName, setTeacherName] = useState('');
  const [teacherId, setTeacherId] = useState(null);

  const deleteTeacher = (event, params) => {
    setTeacherId(params.row.profile_id);
    setTeacherName(params.row.firstname + ' ' + params.row.lastname);
    setDeleteOpen(true);
  };

  const doDeleteTeacher = async () => {
    const response = await fetch(backendUrl + 'api/delete-teacher/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + String(authTokens.access),
      },
      body: JSON.stringify({ id: teacherId }),
    }).catch(console.error);

    if (response?.status === 200) {
      setDeleteOpen(false);
      props.onTeacherDelete();
    } else if (response?.statusText === 'Unauthorized') {
      logoutUser();
    }
  };

  const columns = [
    {
      field: 'firstname',
      headerName: t('common.first_name'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Link to={`/teacher/${params.row.profile_id}`} style={{ textDecoration: 'none' }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: NAV_BG, fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
            {params.value}
          </Typography>
        </Link>
      ),
    },
    {
      field: 'lastname',
      headerName: t('common.last_name'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Link to={`/teacher/${params.row.profile_id}`} style={{ textDecoration: 'none' }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: NAV_BG, fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
            {params.value}
          </Typography>
        </Link>
      ),
    },
    {
      field: 'email',
      headerName: t('common.email'),
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#475569' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'phone_number',
      headerName: t('common.phone_number'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#475569' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'profile_id',
      headerName: t('teachers.actions'),
      width: 100,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={t('teachers.edit')} arrow>
            <Link to={`/teacher/${params.value}`} style={{ display: 'flex' }}>
              <IconButton
                size="small"
                sx={{
                  color: '#64748b',
                  borderRadius: '8px',
                  '&:hover': { color: NAV_BG, background: 'rgba(15,45,94,0.08)' },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title={t('teachers.delete')} arrow>
            <IconButton
              size="small"
              sx={{
                color: '#64748b',
                borderRadius: '8px',
                '&:hover': { color: '#ef4444', background: 'rgba(239,68,68,0.08)' },
              }}
              onClick={(e) => deleteTeacher(e, params)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = props.teachers.map((teacher) => ({ ...teacher, id: teacher.profile_id }));

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(15,45,94,0.07)',
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        localeText={{ ...enUS.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: t('common.no_rows'), noResultsOverlayLabel: t('common.no_rows'), MuiTablePagination: { labelRowsPerPage: t('common.rows_per_page') } }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        slots={{ toolbar: CustomToolbar }}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 20 } },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        autoHeight
        getRowClassName={(params) => (!params.row.is_active ? 'row-inactive' : '')}
        sx={{
          border: 'none',
          fontFamily: '"Outfit", sans-serif',
          '& .MuiDataGrid-columnHeaders': {
            background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)`,
            borderBottom: 'none',
            borderRadius: 0,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            fontSize: '0.8rem',
            letterSpacing: '0.04em',
            color: 'rgba(241,245,249,0.85)',
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus, &:focus-within': { outline: 'none' },
          },
          '& .MuiDataGrid-columnSeparator': {
            color: 'rgba(245,158,11,0.2)',
          },
          '& .MuiDataGrid-row': {
            transition: 'background 0.15s ease',
            '&:hover': {
              background: 'rgba(15,45,94,0.03)',
            },
            '&.Mui-selected': {
              background: `rgba(245,158,11,0.06)`,
              '&:hover': { background: `rgba(245,158,11,0.1)` },
            },
          },
          '& .row-inactive': {
            background: '#fffbeb',
            '&:hover': { background: '#fef9e7' },
          },
          '& .MuiDataGrid-cell': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.875rem',
            color: '#1e293b',
            borderColor: '#f1f5f9',
            '&:focus, &:focus-within': { outline: 'none' },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #e2e8f0',
            background: '#fafbfc',
          },
          '& .MuiTablePagination-root': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.85rem',
            color: '#475569',
          },
        }}
      />

      <ConfirmDialogComponent
        handleAction={doDeleteTeacher}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        text={teacherName}
        texttitle={t('teachers.delete_confirm')}
      />
    </Paper>
  );
};

export default TeachersComponent;
